import { getLocale, getEnv } from '@shopee/global-env';
import { isFeatureEnabled as isStaticFeatureEnabled } from '@shopee-user/static-feature-toggle';
import { Feature as StaticFeature } from '@shopee-user/static-feature-toggle';
import type { IABTestClient } from './types';
import {
  BusinessTargetConfig,
  BusinessTarget,
  Env,
  Locale,
} from '@shopee-user/global';
import { isWebBridgeSupported } from '@shopee/web-bridge-sdk';
import { Feature } from '@shopee-user/feature-toggle';
import { isFeatureEnabled } from '@shopee-user/business-customization-logic';

const isABTestSupported = () => {
  const isWebViewAndABTestSupported =
    isWebBridgeSupported() &&
    isFeatureEnabled(Feature.WebViewABTestSupported, false);
  const isWebAndABTestSupported =
    !isWebBridgeSupported() &&
    isStaticFeatureEnabled(StaticFeature.TobWebTrackingSdkV4Integration, false);
  return isWebViewAndABTestSupported || isWebAndABTestSupported;
};

const loadClient = () => {
  return isWebBridgeSupported()
    ? import(
        /* webpackChunkName: "AbTestNativeClient" */ './AbTestNativeClient'
      )
    : import(/* webpackChunkName: "AbTestWebClient" */ './AbTestWebClient');
};

const { getABTestConfig } = BusinessTargetConfig[
  __BUSINESS_TARGET__ as BusinessTarget
];

let loadABTestClientPromise: Promise<IABTestClient | null> | null = null;

const initABTest = async () => {
  if (loadABTestClientPromise) return loadABTestClientPromise;

  const config = getABTestConfig(getEnv() as Env, getLocale() as Locale);

  loadABTestClientPromise = new Promise(resolve => {
    if (!config || !isABTestSupported()) {
      resolve(null);
      return;
    }

    loadClient()
      .then(module => {
        const initor = module.default;
        const client = initor.createClient(config);
        resolve(client);
      })
      .catch(() => {
        resolve(null);
      });
  });
  return await getABTestClient();
};

const getABTestClient = async () => {
  return loadABTestClientPromise ? await loadABTestClientPromise : null;
};

export { getABTestClient, initABTest };
